import { useMemo, useState } from 'react';

import { CheckCircle2, Clock3, MessageSquare, RotateCcw } from 'lucide-react';

import { useAuth } from '@/nucleo/auth/useAuth';
import { formatarDataHora } from '@/nucleo/formato/datas';
import { Badge } from '@/ui/base/badge';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';

import { rotuloDeSublinha } from '../apontamentos/sublinhas';
import { MOCK_DRES } from '../dados-simulados';

import type { PersonaDaTratativa, TratativaLinha } from './contrato';
import { contarPorSituacao, useTratativas } from './dados';
import { rotuloDaSituacao, SITUACAO_VARIANTE } from './situacao';
import { TratativaDetalhe } from './TratativaDetalhe';

/**
 * `/sme/tratativas` — a conversa entre a SME e as DREs sobre um apontamento
 * devolvido.
 *
 * A tela é uma bandeja: cada linha é uma devolução em aberto, respondida ou já
 * resolvida, e clicar nela abre a conversa inteira. É a mesma grade do
 * protótipo (`SmeTratativasDre.tsx`), com três diferenças conscientes:
 *
 * 1. NÃO HÁ COLUNA DE AÇÕES. O protótipo fecha a linha com um ícone de balão
 *    que não é botão — decoração com aparência de ação. Aqui a linha inteira
 *    abre o detalhe (CLAUDE.md, "Regras de interface", item 9), e um ícone
 *    imitando ação numa coluna de ações seria exatamente o que o item 7 proíbe.
 * 2. A BANDEJA NÃO É SERVIDA À PERSONA DRE — ver abaixo.
 * 3. AS AÇÕES DE ESCRITA VIVEM NO DETALHE, e não gravam — ver
 *    `TratativaDetalhe.tsx`.
 *
 * POR QUE A PERSONA DRE NÃO RECEBE A LISTA. Uma diretoria só pode ver as
 * tratativas da própria diretoria, e nada neste sistema sabe de qual diretoria
 * é quem está logado: `UsuarioAutenticado` (`nucleo/auth/tipos.ts`, o contrato
 * do `GET /eu`) não tem campo de DRE, porque o recorte é da API e do banco.
 * Enquanto essa rota não existe, o mock devolve as tratativas de todas as
 * diretorias — e servir isso a uma DRE, ainda por cima sem a coluna que diria
 * de quem é cada linha, seria mostrar o que ela não pode ver e escondendo que
 * está mostrando.
 *
 * A saída é a mesma já usada na coluna `Saúde` da Parametrização: a estrutura
 * fica, a afirmação que não é verdade some. A persona DRE recebe um estado que
 * explica a lacuna, e a consulta nem chega a rodar. Inventar no mock uma
 * correspondência "este usuário é desta DRE" seria criar aqui uma regra que a
 * API real não terá.
 *
 * A LEITURA AINDA É MOCKADA (`dados.ts`, decisão do dono de 2026-09-22): o
 * modelo de dados existe no banco desde a migração 0002, mas não há rota que o
 * sirva.
 */

const TODAS = 'TODAS';

const FILTROS_PADRAO = {
  tr_situacao: 'ABERTO',
  tr_busca: '',
  tr_dre: '',
};

/** Competência no formato que a grade mostra: `MM/AAAA`. */
function competencia(linha: TratativaLinha): string {
  return `${String(linha.mes).padStart(2, '0')}/${linha.ano}`;
}

export function TratativasSmeDrePage() {
  const { temPapel } = useAuth();
  const { values, setFilter, clearAll } = useUrlFilters(FILTROS_PADRAO);
  const [aberta, setAberta] = useState<TratativaLinha | null>(null);

  /**
   * A persona de quem está lendo. Decide o rótulo da situação, quem pode falar
   * no detalhe e — enquanto o recorte por diretoria não existir na API — se a
   * bandeja é servida.
   */
  const persona: PersonaDaTratativa = temPapel(
    'ADMIN',
    'GESTOR_ADMIN',
    'FINANCEIRO',
    'GESTOR_SME',
    'OPERADOR_SME',
  )
    ? 'SME'
    : 'DRE';

  const ehSme = persona === 'SME';

  /*
    A consulta NÃO leva a situação: os indicadores contam a lista inteira do
    recorte, e contar só o que já está filtrado mostraria sempre o mesmo número
    no cartão selecionado e zero nos outros.

    Fora da SME ela nem roda: sem recorte por diretoria, a lista inteira não
    pode chegar ao navegador de quem só poderia ver uma delas — nem como número
    num indicador.
  */
  const consulta = useTratativas(
    {
      busca: values.tr_busca.trim() || undefined,
      dreId: values.tr_dre || undefined,
    },
    { habilitado: ehSme },
  );

  const linhas = useMemo(() => consulta.data ?? [], [consulta.data]);
  const contagem = useMemo(() => contarPorSituacao(linhas), [linhas]);

  const linhasFiltradas = useMemo(() => {
    if (!values.tr_situacao || values.tr_situacao === TODAS) {
      return linhas;
    }
    return linhas.filter((linha) => linha.situacao === values.tr_situacao);
  }, [linhas, values.tr_situacao]);

  const indicadores: StatItem[] = [
    {
      title: 'Total',
      value: linhas.length,
      icon: MessageSquare,
      description: 'Tratativas no recorte atual',
      onClick: () => setFilter('tr_situacao', TODAS),
      active: !values.tr_situacao || values.tr_situacao === TODAS,
    },
    {
      title: 'Aguardando DRE',
      value: contagem.ABERTO,
      icon: Clock3,
      description: 'Abertas, ainda sem resposta',
      className: contagem.ABERTO > 0 ? 'border-destructive/40 bg-destructive/5' : undefined,
      onClick: () => setFilter('tr_situacao', 'ABERTO'),
      active: values.tr_situacao === 'ABERTO',
    },
    {
      title: 'Respondidas',
      value: contagem.RESPONDIDO,
      icon: RotateCcw,
      description: 'Já respondidas, aguardando encerramento',
      onClick: () => setFilter('tr_situacao', 'RESPONDIDO'),
      active: values.tr_situacao === 'RESPONDIDO',
    },
    {
      title: 'Resolvidas',
      value: contagem.RESOLVIDO,
      icon: CheckCircle2,
      description: 'Conversa encerrada',
      onClick: () => setFilter('tr_situacao', 'RESOLVIDO'),
      active: values.tr_situacao === 'RESOLVIDO',
    },
  ];

  const colunas: TableViewColumn<TratativaLinha>[] = useMemo(
    () => [
      {
        id: 'situacao',
        header: 'Situação',
        isStatusColumn: true,
        isCritical: true,
        canHide: false,
        linkedFilterKeys: ['tr_situacao'],
        cell: (linha) => (
          <Badge variant={SITUACAO_VARIANTE[linha.situacao]}>
            {rotuloDaSituacao(linha.situacao, linha.autorPapel, persona)}
          </Badge>
        ),
      },
      {
        id: 'competencia',
        header: 'Competência',
        sortKey: (linha) => `${linha.ano}-${String(linha.mes).padStart(2, '0')}`,
        cell: competencia,
      },
      {
        id: 'dre',
        header: 'DRE',
        linkedFilterKeys: ['tr_dre'],
        sortKey: (linha) => linha.dreNome,
        cell: (linha) => (
          <span className="text-muted-foreground">
            {linha.dreCodigo} · {linha.dreNome}
          </span>
        ),
      },
      {
        id: 'credenciado',
        header: 'Credenciado / OS',
        isCritical: true,
        sortKey: (linha) => linha.credenciadoNome,
        cell: (linha) => (
          <div className="flex flex-col">
            <span className="font-medium">{linha.credenciadoNome}</span>
            <span className="text-xs text-muted-foreground">{linha.osNumero}</span>
          </div>
        ),
      },
      {
        id: 'sublinha',
        header: 'Sublinha',
        cell: (linha) => rotuloDeSublinha(linha.sublinha),
      },
      {
        id: 'ultima_interacao',
        header: 'Última interação',
        sortKey: (linha) => linha.ultimaMensagemEm,
        cell: (linha) => (
          <div className="flex flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline">{linha.ultimaMensagemAutorPapel}</Badge>
              <span className="text-xs text-muted-foreground">
                {formatarDataHora(linha.ultimaMensagemEm)}
              </span>
            </span>
            <span className="line-clamp-2 text-sm">{linha.ultimaMensagem}</span>
          </div>
        ),
      },
    ],
    [persona],
  );

  const visaoDaTabela = useTableViewPreferences({
    contextKey: 'tratativas:sme',
    columns: colunas,
    filterValues: values,
  });

  if (!ehSme) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Tratativas SME ↔ DRE"
          description="Acompanhe as devoluções de apontamento discutidas entre a SME e as DREs."
        />
        <EmptyState
          variant="messages"
          title="A lista da sua diretoria ainda não está disponível"
          description="Mostrar só as tratativas da sua diretoria depende de uma consulta que ainda está sendo construída. Até lá a lista não é exibida — mostrar as tratativas de todas as diretorias seria pior do que não mostrar nenhuma. Enquanto isso, trate as devoluções pela tela de Apontamentos ou fale com a equipe da SME."
        />
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Tratativas SME ↔ DRE"
          description="Acompanhe as devoluções de apontamento discutidas entre a SME e as DREs."
        />
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar as tratativas"
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tratativas SME ↔ DRE"
        description="Acompanhe as devoluções de apontamento discutidas entre a SME e as DREs."
      />

      {/*
        Região nomeada: os cartões também FILTRAM, então quem navega por teclado
        ou leitor de tela precisa saber que aquele bloco é um grupo de atalhos,
        e não quatro números soltos no topo da página.
      */}
      <section aria-label="Indicadores das tratativas">
        <StatsGrid columns="grid-cols-2 lg:grid-cols-4" stats={indicadores} />
      </section>

      <FilterBar
        fields={[
          {
            key: 'tr_busca',
            label: 'Buscar credenciado ou OS',
            type: 'search',
            placeholder: 'Nome, documento ou número da OS',
            flex: true,
          },
          {
            key: 'tr_dre',
            label: 'DRE',
            type: 'select',
            options: MOCK_DRES.map((dre) => ({
              value: dre.id,
              label: `${dre.codigo} · ${dre.nome}`,
            })),
            allOptionLabel: 'Todas as DREs',
            width: 'w-full sm:w-[240px]',
          },
          {
            key: 'tr_situacao',
            label: 'Situação',
            type: 'select',
            options: [
              { value: 'ABERTO', label: 'Aberta' },
              { value: 'RESPONDIDO', label: 'Respondida' },
              { value: 'RESOLVIDO', label: 'Resolvida' },
            ],
            allOptionLabel: 'Todas as situações',
            width: 'w-full sm:w-[220px]',
          },
        ]}
        values={values}
        defaultValues={FILTROS_PADRAO}
        onChange={setFilter}
        onClear={clearAll}
      />

      <DataTable
        columns={visaoDaTabela.orderedColumns as DataTableColumn<TratativaLinha>[]}
        personalizacao={visaoDaTabela}
        data={linhasFiltradas}
        isLoading={consulta.isLoading}
        onRowClick={(linha) => setAberta(linha)}
        ariaLabel="Tratativas entre a SME e as DREs"
        emptyIcon={MessageSquare}
        emptyTitle="Nenhuma tratativa neste recorte"
        emptyDescription="Ajuste a busca, a DRE ou a situação para ver outras devoluções."
      />

      {aberta && (
        <TratativaDetalhe
          tratativa={aberta}
          persona={persona}
          aberto={aberta !== null}
          aoFechar={() => setAberta(null)}
        />
      )}
    </div>
  );
}
